import { Button as AriaButton, TextField } from "react-aria-components"
import { Search, X } from "lucide-react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

/**
 * THE search box (redesign 2026-10-21): one look everywhere something is searched or filtered — a fully rounded pill at
 * the standard control height (32px, like every other field), a search glyph on the left and a clear button once there
 * is text. Before this, three hand-assembled copies (drill-down filter bar, manuals toolbar, `FilterField`) had drifted
 * apart in radius and height.
 *
 * A labelled react-aria `TextField` over the shared `Input` primitive, so the focus/invalid/dark rules stay the input's.
 * The CALLER owns the text and what a search does; this only renders it.
 */
function SearchField({
  label,
  placeholder,
  value,
  onChange,
  clearLabel,
  onClear,
  onSubmit,
  autoFocus,
  type = "text",
  className,
}: {
  /** Accessible name of the field. */
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
  /** Accessible name of the clear (×) button. */
  clearLabel: string
  /** What × does. Default: empty the field (`onChange("")`). */
  onClear?: () => void
  /** Enter — for fields that LAUNCH a search rather than filter live. The caller decides what blank text means. */
  onSubmit?: (value: string) => void
  autoFocus?: boolean
  /** `"search"` gives the searchbox role (filter fields); the browser's own cancel glyph is hidden either way. */
  type?: "text" | "search"
  className?: string
}) {
  return (
    <TextField aria-label={label} value={value} onChange={onChange} className={cn("relative", className)}>
      <Search aria-hidden="true" className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
      <Input
        type={type}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="bg-muted h-8 rounded-full pr-8 pl-9 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
        onKeyDown={
          onSubmit
            ? (e) => {
                if (e.key === "Enter") onSubmit(value)
              }
            : undefined
        }
      />
      {value !== "" ? (
        <AriaButton
          aria-label={clearLabel}
          onPress={onClear ?? (() => onChange(""))}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer rounded-full p-0.5 outline-none focus-visible:ring-2"
        >
          <X aria-hidden="true" className="size-4" />
        </AriaButton>
      ) : null}
    </TextField>
  )
}

export { SearchField }
